/**
 * B5 (E-1.B.1-05): Dry-Run Preview
 * Pull 5 sample contacts from GHL, apply field mapping, show before/after.
 *
 * B8 (E-1.B.1-08): Field Transform Preview
 * Shows input → transform → output for each transform rule.
 */
import { useState, useEffect } from "react";
import { useTranslation } from "@/i18n";
import { apiFetch } from "@/lib/api-client";
import { cn } from "@/lib/utils";
import type { WizardState } from "../wizard-shell";

interface PreviewStepProps {
  state: WizardState;
  goNext: () => void;
  updateState: (patch: Partial<WizardState>) => void;
}

interface PreviewRecord {
  source: Record<string, unknown>;
  mapped: Record<string, unknown>;
  warnings: string[];
  transforms: Array<{ field: string; input: string; output: string; type: string }>;
}

export function PreviewStep({ state, updateState }: PreviewStepProps) {
  const { t } = useTranslation();
  const [records, setRecords] = useState<PreviewRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [showTransforms, setShowTransforms] = useState(false);

  useEffect(() => {
    if (!state.jobId || records.length > 0) return;

    setIsLoading(true);
    apiFetch<{ records: PreviewRecord[] }>(
      `/platform/migration/${state.jobId}/preview`,
      { method: "POST", body: JSON.stringify({ sampleSize: 5 }) },
    )
      .then((result) => {
        setRecords(result.records);
        updateState({ previewResults: result.records });
      })
      .catch(() => setRecords([]))
      .finally(() => setIsLoading(false));
  }, [state.jobId, records.length, updateState]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center gap-4 py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        <p className="text-sm text-muted-foreground">{t("migration.preview.loading")}</p>
      </div>
    );
  }

  if (records.length === 0) {
    return <p className="text-sm text-muted-foreground">{t("migration.preview.noData")}</p>;
  }

  const current = records[selectedIdx];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">{t("migration.preview.title")}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t("migration.preview.description")}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowTransforms(!showTransforms)} className="text-xs text-primary hover:underline">
            {showTransforms ? t("migration.preview.hideTransforms") : t("migration.preview.showTransforms")}
          </button>
        </div>
      </div>

      {/* Sample selector */}
      <div className="flex gap-2">
        {records.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setSelectedIdx(idx)}
            className={cn(
              "rounded-md px-3 py-1 text-sm font-medium",
              idx === selectedIdx ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-accent",
            )}
          >
            {t("migration.preview.sample")} {idx + 1}
          </button>
        ))}
      </div>

      {/* Warnings */}
      {current && current.warnings.length > 0 && (
        <div className="rounded-md bg-[var(--color-warning)]/5 px-3 py-2 text-sm text-[var(--color-warning)]">
          {current.warnings.map((w, i) => <p key={i}>⚠️ {w}</p>)}
        </div>
      )}

      {/* Before/After comparison */}
      {current && (
        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-lg border border-border p-4">
            <h3 className="mb-2 text-sm font-semibold text-muted-foreground">{t("migration.preview.source")}</h3>
            <dl className="space-y-1">
              {Object.entries(current.source).map(([key, val]) => (
                <div key={key} className="flex gap-2 text-sm">
                  <dt className="w-32 shrink-0 font-medium text-muted-foreground">{key}</dt>
                  <dd className="text-foreground">{String(val ?? "—")}</dd>
                </div>
              ))}
            </dl>
          </div>
          <div className="rounded-lg border border-[var(--color-success)]/20 bg-[var(--color-success)]/5 p-4">
            <h3 className="mb-2 text-sm font-semibold text-[var(--color-success)]">{t("migration.preview.mapped")}</h3>
            <dl className="space-y-1">
              {Object.entries(current.mapped).map(([key, val]) => (
                <div key={key} className="flex gap-2 text-sm">
                  <dt className="w-32 shrink-0 font-medium text-[var(--color-success)]">{key}</dt>
                  <dd className="text-[var(--color-success)]">{String(val ?? "—")}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      )}

      {/* B8: Transform preview */}
      {showTransforms && current && current.transforms.length > 0 && (
        <div className="rounded-lg border border-border p-4">
          <h3 className="mb-3 text-sm font-semibold text-foreground">{t("migration.preview.transforms")}</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground">
                <th className="pb-2">{t("migration.preview.field")}</th>
                <th className="pb-2">{t("migration.preview.input")}</th>
                <th className="pb-2">→</th>
                <th className="pb-2">{t("migration.preview.output")}</th>
                <th className="pb-2">{t("migration.preview.transformType")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {current.transforms.map((tr, i) => (
                <tr key={i}>
                  <td className="py-1.5 text-foreground">{tr.field}</td>
                  <td className="py-1.5 text-muted-foreground">{tr.input}</td>
                  <td className="py-1.5">→</td>
                  <td className="py-1.5 text-foreground">{tr.output}</td>
                  <td className="py-1.5"><span className="rounded bg-muted px-1.5 py-0.5 text-xs">{tr.type}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
